import React from "react";
import {
  ArrowRight,
  Car,
  Bus,
  Anchor,
  ParkingCircle,
  Info,
  MapPin,
  Bike,
  Toilet,
  Wifi,
  Heart,
  Landmark,
  Lightbulb,
} from "lucide-react";
import LocationMap from "@/components/content/LocationMap";
import Seo from "@/components/Seo";
import { LLink } from "@/i18n/LLink";
import { useLang } from "@/i18n/LangContext";
import { pageContent, pickLang } from "@/i18n/pageContent";

const LEMMER_CENTRUM = { lat: 52.8479, lng: 5.7128 };

const FACILITY_ICONS = [Toilet, MapPin, Wifi, Heart];

const ContentEyebrow = ({ children }) => (
  <span className="inline-block text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3">
    {children}
  </span>
);

const AccentLine = () => (
  <div className="flex items-center gap-3 mb-6" aria-hidden="true">
    <span className="block w-10 h-[3px] rounded-full bg-[#2E9CD6]" />
    <svg width="60" height="10" viewBox="0 0 60 10" className="text-[#6BC0E8]">
      <path
        d="M0,5 C10,0 20,10 30,5 C40,0 50,10 60,5"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

const SubItem = ({ icon: Icon, title, children }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 rounded-xl bg-[#F4F8FB] text-[#2E9CD6] flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <h3 className="font-[Poppins] font-bold text-[#173A5E] text-base mb-1">
        {title}
      </h3>
      <div className="text-[#3E5871] font-[Inter] text-[15px] leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  </div>
);

export const PraktischPage = () => {
  const { lang } = useLang();
  const c = pickLang(pageContent.praktisch, lang);

  return (
    <div data-testid="praktisch-page" className="bg-white">
      <Seo title={c.seo.title} description={c.seo.description} lang={lang} />

      {/* Sfeeraccent */}
      <section
        className="relative w-full overflow-hidden"
        style={{ height: "clamp(220px, 34vw, 380px)" }}
      >
        <img
          src="/media/lemmer-kortestreek.jpg"
          alt="Lemmer centrum"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(23,58,94,0.15) 0%, rgba(23,58,94,0.75) 100%)",
          }}
        />
        <div className="absolute inset-0 flex items-end">
          <div className="ll-container pb-10">
            <span
              data-testid="praktisch-hero-eyebrow"
              className="inline-block text-[#6BC0E8] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3"
            >
              {c.heroEyebrow}
            </span>
            <h1
              data-testid="praktisch-h1"
              className="text-white text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight"
            >
              {c.h1}
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 sm:py-20">
        <div className="ll-container max-w-3xl">
          <p className="text-[#3E5871] font-[Inter] text-lg sm:text-xl leading-relaxed">
            {c.intro}
          </p>
        </div>
      </section>

      {/* Bereikbaarheid */}
      <section
        data-testid="praktisch-bereikbaarheid"
        className="ll-section-soft py-14 sm:py-20"
      >
        <div className="ll-container max-w-4xl">
          <ContentEyebrow>{c.bereikbaarheid.eyebrow}</ContentEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            {c.bereikbaarheid.title}
          </h2>
          <AccentLine />

          <div className="space-y-6">
            <SubItem icon={Car} title={c.auto.title}>
              <p>{c.auto.body}</p>
            </SubItem>
            <SubItem icon={Bus} title={c.ov.title}>
              <p>{c.ov.body}</p>
            </SubItem>
            <SubItem icon={Anchor} title={c.water.title}>
              <p>
                {c.water.body1}
                <LLink to="/watersport-haven" className="ll-link">
                  {c.water.link}
                </LLink>
                .
              </p>
            </SubItem>
          </div>
        </div>
      </section>

      {/* Parkeren */}
      <section data-testid="praktisch-parkeren" className="py-14 sm:py-20">
        <div className="ll-container max-w-4xl">
          <ContentEyebrow>{c.parkeren.eyebrow}</ContentEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            {c.parkeren.title}
          </h2>
          <AccentLine />
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F4F8FB] text-[#2E9CD6] flex items-center justify-center shrink-0">
              <ParkingCircle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed">
                {c.parkeren.body}
              </p>
              <div className="mt-6 rounded-2xl bg-[#F4F8FB] border border-[#E4EEF5] p-5 flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-[#2E9CD6] shrink-0 mt-0.5" />
                <div>
                  <p className="font-[Poppins] font-semibold text-[#173A5E] text-sm mb-1">
                    {c.parkeren.tipTitle}
                  </p>
                  <p className="text-[#3E5871] font-[Inter] text-[15px] leading-relaxed">
                    {c.parkeren.tip}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Voorzieningen */}
      <section
        data-testid="praktisch-voorzieningen"
        className="ll-section-soft py-14 sm:py-20"
      >
        <div className="ll-container max-w-4xl">
          <ContentEyebrow>{c.voorzieningen.eyebrow}</ContentEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            {c.voorzieningen.title}
          </h2>
          <AccentLine />
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed mb-8 max-w-3xl">
            {c.voorzieningen.body}
          </p>
          <div className="grid gap-5 sm:grid-cols-2">
            {(c.voorzieningen.items || []).map((item, idx) => {
              const Icon = FACILITY_ICONS[idx % FACILITY_ICONS.length];
              return (
                <div
                  key={item.title}
                  className="rounded-2xl bg-white border border-[#E4EEF5] p-5 flex items-start gap-3 shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#F4F8FB] text-[#2E9CD6] flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-[Poppins] font-bold text-[#173A5E] text-base mb-1">
                      {item.title}
                    </h3>
                    <p className="text-[#3E5871] font-[Inter] text-[15px] leading-relaxed">
                      {item.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Toeristische info & VVV */}
      <section data-testid="praktisch-vvv" className="py-14 sm:py-20">
        <div className="ll-container max-w-4xl">
          <ContentEyebrow>{c.vvv.eyebrow}</ContentEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            {c.vvv.title}
          </h2>
          <AccentLine />
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F4F8FB] text-[#2E9CD6] flex items-center justify-center shrink-0">
              <Landmark className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed mb-3">
                {c.vvv.body}
              </p>
              <LLink to="/agenda" className="ll-link">
                {c.vvv.linkText} <ArrowRight className="inline w-4 h-4" />
              </LLink>
            </div>
          </div>
        </div>
      </section>

      {/* Fietsverhuur */}
      <section
        data-testid="praktisch-fietsverhuur"
        className="ll-section-soft py-14 sm:py-20"
      >
        <div className="ll-container max-w-4xl">
          <ContentEyebrow>{c.fietsverhuur.eyebrow}</ContentEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            {c.fietsverhuur.title}
          </h2>
          <AccentLine />
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F4F8FB] text-[#2E9CD6] flex items-center justify-center shrink-0">
              <Bike className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed mb-3">
                {c.fietsverhuur.body}
              </p>
              <LLink to="/bedrijvengids" className="ll-link">
                {c.fietsverhuur.linkText} <ArrowRight className="inline w-4 h-4" />
              </LLink>
            </div>
          </div>
        </div>
      </section>

      {/* Plattegrond */}
      <section data-testid="praktisch-plattegrond" className="py-14 sm:py-20">
        <div className="ll-container">
          <div className="max-w-3xl mb-6">
            <ContentEyebrow>{c.plattegrond.eyebrow}</ContentEyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">
              {c.plattegrond.title}
            </h2>
            <AccentLine />
            <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed">
              {c.plattegrond.body}
            </p>
          </div>
          <LocationMap
            lat={LEMMER_CENTRUM.lat}
            lng={LEMMER_CENTRUM.lng}
            name="Lemmer centrum"
            zoom={15}
            height={420}
          />
        </div>
      </section>

      {/* Ontdek meer */}
      <section className="ll-section-soft py-12 sm:py-14">
        <div className="ll-container max-w-3xl">
          <p className="text-sm font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594] mb-3">
            {c.ontdekMeer.title}
          </p>
          <div className="flex flex-wrap gap-3">
            <LLink
              to="/zien-en-doen"
              data-testid="praktisch-cta-zien-doen"
              className="ll-btn-primary"
            >
              {c.ontdekMeer.zienDoen}
              <ArrowRight className="w-4 h-4" />
            </LLink>
            <LLink
              to="/eten-en-drinken"
              data-testid="praktisch-cta-eten-drinken"
              className="ll-btn-accent"
            >
              {c.ontdekMeer.etenDrinken}
              <ArrowRight className="w-4 h-4" />
            </LLink>
            <LLink
              to="/watersport-haven"
              data-testid="praktisch-cta-watersport"
              className="ll-btn-outline"
            >
              {c.ontdekMeer.watersport}
              <ArrowRight className="w-4 h-4" />
            </LLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PraktischPage;
